/** On-board add-ons pricing manager — retail, supply cost, auto profit/unit. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function AddOnsManager() {
  const [addOns, setAddOns] = useState([]);
  const [savingCode, setSavingCode] = useState(null);

  useEffect(() => {
    api.get("/admin/add-ons").then(({ data }) => setAddOns(data || [])).catch(() => {});
  }, []);

  const saveAddOn = async (code, patch) => {
    setSavingCode(code);
    try {
      const { data } = await api.patch(`/admin/add-ons/${code}`, patch);
      setAddOns((list) => list.map((a) => (a.code === code ? { ...a, ...data } : a)));
      toast.success(`${data.label} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update add-on");
    } finally {
      setSavingCode(null);
    }
  };

  if (!addOns.length) return null;
  return (
    <div data-testid="addons-config-card" className="bg-[#0d0d0d] border border-[#C5A059]/20 rounded-xl p-5">
      <div className="mb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">On-board add-ons</p>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Retail price is what the guest pays. Supply cost is your out-of-pocket for the item —
          it&apos;s <span className="text-zinc-200">reimbursed in full to the driver</span>{" "}
          during payout, and only the profit (retail − supply) gets the 80/20 split.
        </p>
      </div>
      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
              <th className="py-2 pr-2">Item</th>
              <th className="py-2 px-2 text-right">Retail (€)</th>
              <th className="py-2 px-2 text-right">Supply cost (€)</th>
              <th className="py-2 pl-2 text-right">Profit / unit</th>
            </tr>
          </thead>
          <tbody>
            {addOns.map((a) => {
              const profit = Math.max(0, (Number(a.price) || 0) - (Number(a.supply_cost) || 0));
              return (
                <tr key={a.code} data-testid={`addon-row-${a.code}`} className="border-b border-white/5 last:border-0">
                  <td className="py-2 pr-2">
                    <div className="text-zinc-100">{a.label}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{a.code}</div>
                  </td>
                  <td className="py-2 px-2 text-right">
                    <Input
                      type="number" min="0" max="500" step="0.5"
                      data-testid={`addon-price-${a.code}`}
                      defaultValue={a.price}
                      key={`p-${a.code}-${a.price}`}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v) && v !== a.price) saveAddOn(a.code, { price: v });
                      }}
                      className="bg-zinc-900 border-white/10 text-sm h-9 tabular-nums w-24 ml-auto text-right"
                    />
                  </td>
                  <td className="py-2 px-2 text-right">
                    <Input
                      type="number" min="0" max="500" step="0.5"
                      data-testid={`addon-supply-${a.code}`}
                      defaultValue={a.supply_cost}
                      key={`s-${a.code}-${a.supply_cost}`}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v) && v !== a.supply_cost) saveAddOn(a.code, { supply_cost: v });
                      }}
                      className="bg-zinc-900 border-white/10 text-sm h-9 tabular-nums w-24 ml-auto text-right"
                    />
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums">
                    <span className={profit > 0 ? "text-emerald-400" : "text-zinc-500"}>€{profit.toFixed(2)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">
        {savingCode ? `saving ${savingCode}…` : "changes save on blur"}
      </p>
    </div>
  );
}
